/**
 * setupErrors — friendly-message mapping for the first-run setup wizard.
 *
 * The setup-token-gated endpoints (and the securex auth layer in front of the
 * WebAuthn register routes) surface raw transport/auth fingerprints on failure —
 * "unauthenticated", "Valid bearer token required", "invalid or missing setup
 * token" — which are meaningless (and slightly alarming) to an operator running
 * first-run setup. `friendlySetupError` collapses those auth-ish failures to a
 * single actionable message and otherwise passes a genuine message through, so
 * the wizard steps never render a bare server fingerprint verbatim.
 */

/** Lowercased raw auth/session fingerprints the setup endpoints can surface. */
const AUTH_FINGERPRINTS = [
  'unauthenticated',
  'valid bearer token required',
  'invalid or missing setup token',
  'authentication required',
  'no active pam subject',
]

/** Single actionable message for any auth/session failure during setup. */
const SETUP_AUTH_MESSAGE =
  'Your setup session is no longer authorized — it may have expired, or setup may already be complete. Reopen the setup link (or re-enter the setup token) shown in the server console, then try again.'

/**
 * Maps an unknown thrown value to a human-readable setup-wizard message.
 *
 * Auth/session failures — a known raw auth fingerprint, or a 401/403 status
 * echoed into the message — collapse to a single actionable message. Any other
 * genuine Error message is passed through; a non-Error value (or an empty
 * message) falls back to `fallback`.
 */
export function friendlySetupError(e: unknown, fallback: string): string {
  const raw = e instanceof Error ? e.message : ''
  const lower = raw.toLowerCase()
  const looksAuth =
    AUTH_FINGERPRINTS.some((f) => lower.includes(f)) || /\b40[13]\b/.test(raw)
  if (looksAuth) return SETUP_AUTH_MESSAGE
  return raw.trim() || fallback
}
